import { Type } from '@sinclair/typebox';
import {
    Address,
    AssetClass,
    CountryCode,
    GovernmentId,
    RefLink,
    StateCode,
} from '../types';

export const InvestmentPriority = Type.Union([
    Type.Literal('AVOID_LOSSES'),
    Type.Literal('BALANCE_RISK'),
    Type.Literal('MAXIMIZE_RETURNS'),
]);

const InvestmentHorizon = Type.Union([
    Type.Literal('SHORT_TERM'),
    Type.Literal('MEDIUM_TERM'),
    Type.Literal('LONG_TERM'),
]);

export const DownturnReaction = Type.Union([
    Type.Literal('SELL'),
    Type.Literal('HOLD'),
    Type.Literal('BUY'),
]);

const LiquidNetWorth = Type.Object({
    amount: Type.Number(),
    currency: Type.Literal('USD'),
});

const AnnualGrossIncome = Type.Object({
    amount: Type.Optional(Type.Number()),
    currency: Type.Literal('USD'),
});

export const UserFinancialProfile = Type.Object({
    investment_priority: Type.Optional(InvestmentPriority),
    investment_horizon: Type.Optional(InvestmentHorizon),
    downturn_reaction: Type.Optional(DownturnReaction),
    liquid_net_worth: Type.Optional(LiquidNetWorth),
    annual_gross_income: Type.Optional(AnnualGrossIncome),
});

export const UserRecommendations = Type.Object({
    recommended_risk_score: Type.Optional(Type.Integer()),
    recommended_asset_allocation: Type.Array(
        Type.Object({
            percentage: Type.Optional(Type.Number()),
            asset_class: AssetClass,
        })
    ),
});

// TODO: Should we add Principles that we don't ask?
// https://docs.atomicvest.com/docs/esg-menu
export const UserInvestmentPrinciple = Type.Union([
    Type.Literal('EMPOWER_WOMEN'),
    Type.Literal('END_HUNGER'),
    Type.Literal('END_POVERTY'),
    Type.Literal('ENSURE_EDUCATION'),
    Type.Literal('ENSURE_HEALTH'),
    Type.Literal('EXCLUDE_ANIMAL_CRUELTY'),
    Type.Literal('EXCLUDE_CIVILIAN_FIREARMS'),
    Type.Literal('EXCLUDE_GAMBLING'),
    Type.Literal('EXCLUDE_HAZARDOUS_PESTICIDES'),
    Type.Literal('EXCLUDE_MILITARY'),
    Type.Literal('EXCLUDE_PREDATORY_LENDING'),
    Type.Literal('EXCLUDE_PRIVATE_PRISONS'),
    Type.Literal('EXCLUDE_TOBACCO'),
    Type.Literal('MAKE_CITIES_SUSTAINABLE'),
    Type.Literal('PROTECT_MARINE_LIFE'),
    Type.Literal('PROTECT_TERRESTRIAL_LIFE'),
    Type.Literal('REDUCE_INEQUALITY'),
    Type.Literal('TAKE_CLIMATE_ACTION'),
]);

export const TaxIdType = Type.Union([
    Type.Literal('USA_SSN'),
    Type.Literal('USA_EIN'),
    Type.Literal('AUSTRALIA_TFN'),
    Type.Literal('AUSTRALIA_ABN'),
    Type.Literal('GERMANY_TAX_ID'),
    Type.Literal('FRANCE_SPI'),
    Type.Literal('UK_UTR'),
    Type.Literal('UK_NINO'),
    Type.Literal('INDIA_PAN'),
    Type.Literal('ISRAEL_TAX_ID'),
    Type.Literal('ITALY_TAX_ID'),
    Type.Literal('JAPAN_KOJIN_BANGO'),
    Type.Literal('NETHERLANDS_TIN'),
    Type.Literal('SINGAPORE_NRIC'),
    Type.Literal('SINGAPORE_FIN'),
    Type.Literal('SINGAPORE_ASDG'),
    Type.Literal('SINGAPORE_ITR'),
    Type.Literal('SWEDEN_PIN'),
    Type.Literal('PHILLIPINES_TAX_ID'),
    Type.Literal('CANADA_TAX_ID'),
    Type.Literal('ARGENTINA_TAX_ID'),
    Type.Literal('SWITZERLAND_TAX_ID'),
    Type.Literal('SOUTH_AFRICA_TAX_ID'),
    Type.Literal('BRAZIL_CPF'),
    Type.Literal('PAKISTAN_TAX_ID'),
    Type.Literal('OTHER_TAX_ID'),
]);

export const MaritalStatus = Type.Union([
    Type.Literal('single'),
    Type.Literal('married'),
    Type.Literal('widowed'),
    Type.Literal('divorced'),
    Type.Literal('civil_partnership'),
]);

export const UserTaxProfile = Type.Object({
    country_of_tax_residence: Type.Optional(CountryCode),
    state_of_tax_residence: Type.Optional(StateCode),
    tax_id: Type.Optional(Type.String()),
    tax_id_type: Type.Optional(TaxIdType),
    marginal_tax_rate: Type.Optional(Type.Number()),
    short_term_capital_gains_rate: Type.Optional(Type.Number()),
    long_term_capital_gains_rate: Type.Optional(Type.Number()),
    marital_status: Type.Optional(MaritalStatus),
});

export const UserResidenceStatus = Type.Union([
    Type.Literal('CITIZEN'),
    Type.Literal('RESIDENT'),
    Type.Literal('PERMANENT_RESIDENT'),
    Type.Literal('NON_RESIDENT'),
    Type.Literal('EMPLOYMENT_VISA'),
    Type.Literal('NONE'),
    Type.Literal('OTHER'),
]);

export const UserIdentity = Type.Object({
    first_name: Type.Optional(Type.String()),
    last_name: Type.Optional(Type.String()),
    email: Type.Optional(Type.String({ format: 'email' })),
    phone_number: Type.Optional(Type.String()),
    date_of_birth: Type.Optional(Type.String({ format: 'date' })),
    address: Type.Optional(Address),
    residence_status: Type.Optional(UserResidenceStatus),
    country_of_citizenship: Type.Optional(Type.Array(CountryCode)),
    gender: Type.Optional(
        Type.Union([
            Type.Literal('male'),
            Type.Literal('female'),
            Type.Literal('other'),
        ])
    ),
    government_ids: Type.Optional(Type.Array(GovernmentId)),
});

export const UserEmployment = Type.Object({
    status: Type.Union([
        Type.Literal('EMPLOYED'),
        Type.Literal('UNEMPLOYED'),
        Type.Literal('RETIRED'),
        Type.Literal('SELF_EMPLOYED'),
        Type.Literal('STUDENT'),
        Type.Literal('HOME_MAKER'),
    ]),
    manual_employer_information: Type.Optional(
        Type.Object({
            employer_name: Type.Optional(Type.String()),
            employer_address: Type.Optional(Address),
            start_date: Type.Optional(Type.String()), // year-month in YYYY-MM format
        })
    ),
});

export const UserAffiliations = Type.Object({
    control_corporations: Type.Optional(
        Type.Array(
            Type.Object({
                corporation_name: Type.String(),
            })
        )
    ),
    affiliated_exchange: Type.Optional(
        Type.Array(
            Type.Object({
                exchange_mic: Type.Union([
                    Type.Literal('XNYS'),
                    Type.Literal('XNAS'),
                    Type.Literal('XNDQ'),
                    Type.Literal('XNFI'),
                    Type.Literal('XNGS'),
                    Type.Literal('XNIM'),
                ]),
            })
        )
    ),
    is_finra_affiliated: Type.Boolean(),
    is_politically_exposed: Type.Boolean(),
    is_family_politically_exposed: Type.Boolean(),
});

export const UserRestrictions = Type.Array(
    Type.Object({
        ticker_symbol: Type.String(),
    })
);

export const UserTrustedContact = Type.Object({
    first_name: Type.Optional(Type.String()),
    last_name: Type.Optional(Type.String()),
    email: Type.Optional(Type.String({ format: 'email' })),
    phone_number: Type.Optional(Type.String()),
    address: Type.Optional(Address),
});

export const UserOccupation = Type.Object({
    title: Type.String(),
    industry: Type.String(),
});

export const UserVerificationStatus = Type.Object({
    identity_verification: Type.Object({
        code: Type.Union([
            Type.Literal('IDENTITY_VERIFICATION_NOT_STARTED'),
            Type.Literal('IDENTITY_VERIFICATION_IN_PROGRESS'),
            Type.Literal('IDENTITY_VERIFICATION_FAILED'),
            Type.Literal('IDENTITY_VERIFICATION_SUCCESSFUL'),
        ]),
    }),
    document_verification: Type.Object({
        code: Type.Union([
            Type.Literal('DOCUMENT_VERIFICATION_NOT_STARTED'),
            Type.Literal('DOCUMENT_VERIFICATION_IN_PROGRESS'),
            Type.Literal('DOCUMENT_VERIFICATION_FAILED'),
            Type.Literal('DOCUMENT_VERIFICATION_SUCCESSFUL'),
        ]),
    }),
});

export const UserLinksAgreements = Type.Array(RefLink);

export const UserLinksAccounts = Type.Array(
    Type.Object({
        id: Type.String(),
        href: Type.String(),
        account_type: Type.Union([
            Type.Literal('INDIVIDUAL'),
            Type.Literal('JOINT'),
            Type.Literal('IRA_TRADITIONAL'),
            Type.Literal('IRA_ROTH'),
            Type.Literal('IRA_SEP'),
            Type.Literal('CORPORATE'),
            Type.Literal('CUSTODIAL'),
            Type.Literal('INDIVIDUAL_529'),
            Type.Literal('TRUST'),
        ]),
    })
);

export const User = Type.Object({
    id: Type.String(),
    created_timestamp: Type.String({ format: 'date-time' }),
    last_modified_timestamp: Type.String({ format: 'date-time' }),
    custom_user_id: Type.String(),
    financial_profile: Type.Optional(UserFinancialProfile),
    recommendations: Type.Optional(UserRecommendations),
    investment_principles: Type.Optional(Type.Array(UserInvestmentPrinciple)),
    tax_profile: Type.Optional(UserTaxProfile),
    identity: Type.Optional(UserIdentity),
    employment: Type.Optional(UserEmployment),
    affiliations: Type.Optional(UserAffiliations),
    restrictions: Type.Optional(UserRestrictions),
    trusted_contact: Type.Optional(UserTrustedContact),
    verification_status: Type.Optional(UserVerificationStatus),
    links_agreements: Type.Optional(UserLinksAgreements),
    links_accounts: Type.Optional(UserLinksAccounts),
    occupation: Type.Optional(UserOccupation),
});

export const UserInput = Type.Object({
    custom_user_id: Type.Optional(Type.String()),
    financial_profile: Type.Optional(UserFinancialProfile),
    investment_principles: Type.Optional(Type.Array(UserInvestmentPrinciple)),
    tax_profile: Type.Optional(UserTaxProfile),
    identity: Type.Optional(UserIdentity),
    employment: Type.Optional(UserEmployment),
    affiliations: Type.Optional(UserAffiliations),
    restrictions: Type.Optional(UserRestrictions),
    trusted_contact: Type.Optional(UserTrustedContact),
    occupation: Type.Optional(UserOccupation),
});

export const CompleteUserInput = Type.Object({
    financial_profile: Type.Object({
        investment_priority: InvestmentPriority,
        investment_horizon: InvestmentHorizon,
        downturn_reaction: DownturnReaction,
        annual_gross_income: AnnualGrossIncome,
        liquid_net_worth: Type.Optional(LiquidNetWorth),
    }),
    investment_principles: Type.Array(UserInvestmentPrinciple),
    tax_profile: UserTaxProfile,
    identity: Type.Object({
        first_name: Type.String(),
        last_name: Type.String(),
        email: Type.String({ format: 'email' }),
        phone_number: Type.String(),
        date_of_birth: Type.String({ format: 'date' }),
        address: Address,
        residence_status: UserResidenceStatus,
        country_of_citizenship: Type.Array(CountryCode),
        gender: Type.Optional(
            Type.Union([
                Type.Literal('male'),
                Type.Literal('female'),
                Type.Literal('other'),
            ])
        ),
    }),
    employment: UserEmployment,
    affiliations: UserAffiliations,
    restrictions: Type.Optional(UserRestrictions),
    trusted_contact: Type.Optional(UserTrustedContact),
    occupation: Type.Optional(UserOccupation),
});
